import { Skeleton } from '@/components/ui/skeleton'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { cn } from '@/lib/utils'
import { User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Patient } from '../types/patient'

type PatientCardVariant = 'full' | 'summary' | 'selected'

type Props = {
  patient?: Patient
  isLoading?: boolean
  variant?: PatientCardVariant
}

function InfoItem({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="min-w-0 rounded-md border border-border/30 bg-secondary/30 px-2 py-1.5">
      <p className="text-[10px] text-muted-foreground">{label}</p>
      <p className="truncate text-xs font-medium">{value || '-'}</p>
    </div>
  )
}

export function PatientCard({ patient, isLoading, variant = 'summary' }: Props) {
  const { t } = useTranslation()
  const { formatDateTime } = useDateFormatter()

  if (isLoading) {
    return (
      <div className="rounded-lg border p-3">
        <div className="flex gap-3">
          <Skeleton className="size-10 rounded-md" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-32" />
          </div>
        </div>

        <Skeleton className="mt-3 h-16 w-full" />
      </div>
    )
  }

  if (!patient) return null

  const birthDate = patient.birthDate
    ? formatDateTime(new Date(patient.birthDate), {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '-'

  const sex = patient.sex === 'M' ? t('common.male') : patient.sex === 'F' ? t('common.female') : '-'

  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        variant === 'selected'
          ? 'border-primary/30 bg-primary/5'
          : variant === 'summary'
            ? 'bg-card border-none p-0'
            : 'bg-card border-border/50 shadow-sm',
      )}
    >
      {/* Header */}
      <div className="flex min-w-0 items-start gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <User className="size-5" />
        </div>

        <div className="min-w-0 flex-1">
          {variant === 'selected' && (
            <p className="text-[10px] text-muted-foreground">{t('scans.usage.selectedPatient')}</p>
          )}

          <p className="truncate text-sm font-semibold">{patient.fullName || '-'}</p>

          <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground">
            HN {patient.hn || '-'} | Card ID {patient.cardId || '-'}
          </p>
        </div>
      </div>

      {/* Selected variant only */}
      {variant === 'selected' && (
        <>
          <div
            className={cn(
              'mt-3 rounded-md border px-3 py-2',
              patient.bedId ? 'border-success/30 bg-success/10' : 'border-destructive/30 bg-destructive/10',
            )}
          >
            <p className={cn('text-[10px] font-medium', patient.bedId ? 'text-success' : 'text-destructive')}>
              {t('scans.usage.bedDepartment')}
            </p>

            <p className="text-sm font-semibold">{patient.bedName || t('scans.usage.noBed')}</p>

            <p className="text-xs text-muted-foreground">{patient.departmentName || '-'}</p>
          </div>

          <div className="mt-2 grid grid-cols-2 gap-2">
            {[
              { label: t('scans.usage.sex'), value: patient.sex || '-' },
              {
                label: t('scans.usage.birthDate'),
                value: formatDateTime(new Date(patient.birthDate), {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                }),
              },
              { label: t('scans.usage.phone'), value: patient.phoneNumber || '-' },
              { label: t('scans.usage.note'), value: patient.note || '-', wide: true },
            ].map(field => (
              <div
                key={field.label}
                className={cn('min-w-0 rounded-md bg-background/60 px-2 py-1.5', field.wide && 'col-span-2')}
              >
                <p className="text-[9px] text-muted-foreground">{field.label}</p>
                <p className="truncate text-[11px] font-medium text-foreground">{field.value}</p>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Summary */}
      {variant === 'summary' && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <InfoItem label={t('scans.usage.sex')} value={sex} />
          <InfoItem label={t('scans.usage.birthDate')} value={birthDate} />
          <InfoItem label={t('scans.usage.phone')} value={patient.phoneNumber} />
          <InfoItem label={t('patient.nationality')} value={patient.nationality} />
          <InfoItem label={t('patient.bloodGroup')} value={patient.bloodGroup} />
          <InfoItem label={t('patient.historyMedicine')} value={patient.historyMedicine} />
        </div>
      )}

      {/* Full */}
      {variant === 'full' && (
        <div className="mt-4 space-y-4">
          <div>
            <p className="mb-2 text-xs font-semibold text-muted-foreground">{t('patient.basicInformation')}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <InfoItem label={t('scans.usage.sex')} value={sex} />
              <InfoItem label={t('patient.birthdate')} value={birthDate} />
              <InfoItem label={t('patient.phoneNumber')} value={patient.phoneNumber} />
              <InfoItem label={t('patient.email')} value={patient.email} />
              <InfoItem label={t('patient.nationality')} value={patient.nationality} />
              <InfoItem label={t('patient.race')} value={patient.race} />
              <InfoItem label={t('patient.religion')} value={patient.religion} />
              <InfoItem label={t('patient.job')} value={patient.job} />
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold text-muted-foreground">{t('patient.medicalInformation')}</p>

            <div className="grid grid-cols-2 sm:grid-cols-2 gap-2">
              <InfoItem label={t('patient.bloodGroup')} value={patient.bloodGroup} />
              <InfoItem label={t('patient.historyMedicine')} value={patient.historyMedicine} />
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold text-muted-foreground">{t('patient.emergencyContact')}</p>

            <div className="grid grid-cols-2 sm:grid-cols-2 gap-2">
              <InfoItem label={t('patient.emergencyName')} value={patient.emergencyName} />
              <InfoItem label={t('patient.emergencyRelation')} value={patient.emergencyRelation} />
              <InfoItem label={t('patient.emergencyPhoneNumber')} value={patient.emergencyPhoneNumber} />
            </div>
          </div>

          {patient.note && <InfoItem label={t('patient.note')} value={patient.note} />}
        </div>
      )}
    </div>
  )
}
